
  import {computed, defineComponent, onMounted, PropType, reactive, toRefs, ref} from "vue";
  import Employee from "@/model/Employee";
  import {t} from "@/mixin/mixins";
  import {toTimeFormat} from "@/WorklogUtils";
  import OrderOperatorsListOperatorNumber from "@/views/Orders/Operators/OrderOperatorsListOperatorNumber.vue";
  import OrderOperatorsListNumberOfPositions from "@/views/Orders/Operators/OrderOperatorsListNumberOfPositions.vue";
  import OrderOperatorsListQualityLevel from "@/views/Orders/Operators/OrderOperatorsListQualityLevel.vue";
  import OrderOperatorsListValueOfGoods from "@/views/Orders/Operators/OrderOperatorsListValueOfGoods.vue";
  import {orderUuid} from "@/RouterUtils";
  import OrderOperatorService, {OrderOperatorV3Dto} from "@/service/OrderOperatorService";
  import OrderServiceV2, {OrderWorklogV3Dto} from "@/service/OrderServiceV2";
  import {groupBy, zeroIfNull} from "@/Utils";
  import Order from "@/model/Order";
  import {yearMonth} from "@/DateUtils";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";
  import {earliestValidFrom} from "@/store/StoreUtils";
  import {init} from "@/views/Orders/Operators/OrderOperatorsComposable";
  import {WorklogType} from "@/enums";

  export interface OrderOperator {
    id?: string;
    employee: Employee;
    operatorNumber?: number;
    numberOfPositions?: number;
    qualityLevel?: string;
    valueOfGoods?: number;
  }

  export default defineComponent({
    name: "OrderOperators",
    components: {
      OrderOperatorsListNumberOfPositions,
      OrderOperatorsListOperatorNumber,
      OrderOperatorsListQualityLevel,
      OrderOperatorsListValueOfGoods
    },
    props: {
      order: {
        type: Object as PropType<Order>,
        default: () => null,
      }
    },
    setup(props) {
      let fileInput = ref<HTMLInputElement | null>(null);

      const initialState = {
        orderUuid: null as string | null,
        orderOperators: [] as OrderOperatorV3Dto[],
        worklogs: [] as OrderWorklogV3Dto[]
      };

      const {
        createOrUpdate,
      } = init();

      const state = reactive({...initialState});

      const footerProps = {
        "items-per-page-options": [-1]
      };

      const headers = [
        {
          text: t("order.operators.operatorNumber"),
          align: "left",
          value: "operatorNumber",
          width: 180
        },
        {
          text: t("name"),
          align: "left",
          value: "name"
        },
        {
          text: t("workingTime"),
          align: "left",
          value: "workingTime"
        },
        {
          text: t("order.operators.qualityLevel"),
          align: "left",
          value: "qualityLevel"
        },
        {
          text: t("order.operators.numberOfPositions"),
          align: "left",
          value: "numberOfPositions",
          width: 150
        },
        {
          text: t("order.operators.valueOfGoods"),
          align: "left",
          value: "valueOfGoods",
          width: 200
        },
        {
          text: t("order.operators.positionsPerHour"),
          align: "right",
          value: "positionsPerHour"
        }
      ];

      const tableOptions = {
        itemsPerPage: -1,
        sortBy: ["operatorNumber"],
        sortDesc: [true]
      };

      const isEditable = computed(() => {
        let order = props.order;
        return order && order.validTo && yearMonth(order.validTo).isSameOrAfter(yearMonth(earliestValidFrom.value));
      });

      const loadItems = () => {
        state.orderUuid = orderUuid.value;
        OrderOperatorService.getOrderOperatorsV3(orderUuid.value)
          .then(response => {
            state.orderOperators = response.data.operators;
          });
        OrderServiceV2.findOrderWorklogsV3(orderUuid.value, null, [WorklogType.WORK])
          .then(worklogs => {
            state.worklogs = worklogs;
          });
      };

      const fullName = (item: OrderOperator) => {
        return item.employee.firstName + " " + item.employee.lastName;
      };

      // Go over all worklogs to calculate the working minutes per employee, keyed by the employee's uuid
      const employeeMap = computed(() => {
        let keyFunction = (worklog: OrderWorklogV3Dto) => worklog.employee.id;
        let valueFunction = (currentValue: number | null | undefined, newValue: OrderWorklogV3Dto) => {
          return currentValue ? currentValue + newValue.minutes : newValue.minutes;
        };

        return groupBy<OrderWorklogV3Dto, string, number>(state.worklogs, keyFunction, valueFunction);
      });

      const items = computed(() => {
        let items = new Array<OrderOperator>();
        employeeMap.value.forEach((value, key) => {
          let worklogEmployee = state.worklogs.find(worklog => worklog.employee.id == key)!.employee;
          let employee = {
            uuid: worklogEmployee.id,
            firstName: worklogEmployee.firstName,
            lastName: worklogEmployee.lastName
          } as Employee;

          let operator = state.orderOperators.find(operator => operator.employee.id == employee.uuid);

          // If we have no entry for that employee yet we create a new one
          let item = {
            id: operator ? operator.id : null,
            employee: employee,
            operatorNumber: operator ? operator.operatorNumber : null,
            numberOfPositions: operator ? operator.numberOfPositions : null,
            qualityLevel: operator ? operator.qualityLevel : null,
            valueOfGoods: operator ? operator.valueOfGoods : null,
          } as OrderOperator;
          items.push(item);
        });

        // Sort by operator number first, then first name in ascending order
        items.sort((a, b) => {
          let diff = zeroIfNull(a.operatorNumber) - zeroIfNull(b.operatorNumber);
          if (diff == 0) {
            // @ts-ignore
            return a.employee.firstName.localeCompare(b.employee.firstName);
          } else {
            return diff;
          }
        });

        return items;
      });

      const workingTimeSummary = computed(() => {
        return Array.from(employeeMap.value.values())
          .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
      });

      const positionCountSummary = computed(() => {
        return state.orderOperators
          .map(orderOperator => orderOperator.numberOfPositions || 0)
          .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
      });

      const valueOfGoodsSum = computed(() => {
        return state.orderOperators
          .map(orderOperator => orderOperator.valueOfGoods || 0)
          .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
      });


      const positionsPerHourSummary = computed(() => {
        let totalMinutes = workingTimeSummary.value;
        if (totalMinutes && totalMinutes > 0) {
          return positionCountSummary.value ? positionCountSummary.value / (totalMinutes / 60) : 0;
        } else {
          return 0;
        }
      });

      const positionsPerHour = (item: OrderOperator) => {
        let minutes = employeeMap.value.get(item.employee.uuid);
        if (minutes && minutes > 0) {
          return item.numberOfPositions ? item.numberOfPositions / (minutes / 60) : 0;
        } else {
          return 0;
        }
      };

      const updateOperator = (updated: OrderOperatorV3Dto) => {
        let index = state.orderOperators.findIndex(operator => operator.id == updated.id);
        if (index >= 0) {
          state.orderOperators.splice(index, 1, updated);
        } else {
          state.orderOperators.push(updated);
        }
      };

      const onImport = () => {
        fileInput?.value?.click();
      };

      const readFile = (event: Event) => {
        console.log("readFile");
        const fileInput = event?.target as HTMLInputElement;
        const file = fileInput?.files?.[0];
        if (file && file.type === "text/csv") {
          readFileEncoding(file).then((csvContent) => {
            console.log(csvContent);
            const rows = csvContent.split("\n").filter(row => row.trim() !== "");

            let promises = new Array<Promise<void>>();
            rows.slice(1).forEach((row) => {
              const values = row.split(";").map(value => value.trim());
              if (values.every(value => value === "")) return;

              let operatorNumber = Number(values[0]);
              // An empty or zero value is left empty, v3 only accepts positive numbers
              let numberOfPositions = parseInt(values[1], 10) || undefined;
              // Replace european notation with english one
              let valueOfGoods = parseFloat(values[2].replace(/\./g, "").replace(",", ".")) || undefined;
              console.log(operatorNumber, numberOfPositions, valueOfGoods);

              let found = items.value.find(item => item.id && item.operatorNumber == operatorNumber);
              if (found) {
                promises.push(createOrUpdate(state.orderUuid!, found, {numberOfPositions, valueOfGoods})
                  .then(updateOperator));
              }
            });
            Promise.all(promises)
              .then(_ => {
                // window.location.reload();
              })
              .catch(_ => {
                eventBus.$emit(
                  MessageTypes.INFO,
                  t("order.orderInfo.tasks.importTasksErrorTitle"),
                  t("order.orderInfo.tasks.importTasksErrorText")
                );
              });
            fileInput.value = "";
          });
        } else {
          eventBus.$emit(
            MessageTypes.INFO,
            t("order.orderInfo.tasks.importTasksErrorTitle"),
            t("order.orderInfo.tasks.importTasksErrorText")
          );
        }
      };

      const readFileEncoding = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event: ProgressEvent<FileReader>) => {
            const buffer = event.target?.result as ArrayBuffer;
            const utf8Decoder = new TextDecoder("utf-8");
            const utf8Text = utf8Decoder.decode(buffer);
            if (containsInvalidUtf8Characters(utf8Text)) {
              console.warn("UTF-8 failed, attempting ISO 8859-1...");
              const isoDecoder = new TextDecoder("iso-8859-1");
              const isoText = isoDecoder.decode(buffer);
              resolve(isoText);
            } else {
              resolve(utf8Text);
            }
          };
          reader.onerror = () => reject(new Error("File reading error"));
          reader.readAsArrayBuffer(file);
        });
      };

      const containsInvalidUtf8Characters = (text: string): boolean => {
        return [...text].some(char => char === "\ufffd");
      };

      onMounted(() => {
        loadItems();
      });

      return {
        ...toRefs(state),
        footerProps,
        headers,
        tableOptions,
        isEditable,
        fullName,
        positionsPerHour,
        toTimeFormat,
        updateOperator,
        fileInput,
        readFile,
        onImport,
        employeeMap,
        items,
        workingTimeSummary,
        positionCountSummary,
        valueOfGoodsSum,
        positionsPerHourSummary
      };
    }
  });
